import React, { useState } from 'react'
import { connect } from 'react-redux'
import { Route, withRouter } from 'react-router-dom'
import { bindActionCreators } from 'redux'

import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip'

import AberdeenSymbol from '../../components/AberdeenSymbol'

import {
  requestSignOut,
  requestNavigation
} from '../../stores/auth'

import {
  UPLOADING,
  COMPLETE,
  FAILED,
  QUEUED
} from '../../stores/uploads'

import './index.css'

function AuthRoute({
  component: C,
  accountName,
  contactName,
  contactEmail,
  showUploadsTab,
  uploads,
  requestSignOut,
  requestNavigation,
  ...rest
}) {
  const [reportOpen, setReportOpen] = useState(false)

  let pending = 0, complete = 0, failed = 0

  for (const [, value] of Object.entries(uploads)) {
    if (value.status === UPLOADING || value.status === QUEUED) pending++
    if (value.status === COMPLETE) complete++
    if (value.status === FAILED) failed++
  }

  const initials = contactName
    ? contactName.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase()
    : '?'

  const navItems = [
    { label: 'Deliverables', path: '/', isActive: pathname => pathname === '/' || pathname.startsWith('/view') },
    ...(showUploadsTab
      ? [{ label: 'Uploads', path: '/uploads', isActive: pathname => pathname.startsWith('/uploads') }]
      : []),
  ]

  const uploadCount = pending + complete + failed
  const countColor =
    failed > 0 ? 'bg-destructive' : pending > 0 ? 'bg-primary' : 'bg-success'

  return (
    <Route
      {...rest}
      render={props => (
        <div className="flex min-h-full flex-col">
          {/* ── Header ── */}
          <header className="sticky top-0 z-30 border-b bg-card">
            <div className="flex h-14 items-center gap-1 px-3 sm:px-4">
              <a
                href="https://aberdeen.io"
                target="_blank"
                rel="noopener noreferrer"
                className="mr-3 flex shrink-0 items-center gap-2 text-inherit no-underline"
              >
                <AberdeenSymbol className="inline-flex h-11 w-[47px] shrink-0 items-center" />
                <span className="text-base font-semibold tracking-tight">Aberdeen Hub</span>
              </a>

              <nav className="hidden flex-1 items-center gap-1 sm:flex">
                {navItems.map(item => {
                  const isActive = item.isActive(props.location.pathname)
                  const showCount = item.path === '/uploads' && uploadCount > 0
                  return (
                    <button
                      key={item.path}
                      onClick={() => requestNavigation(item.path)}
                      className={cn(
                        'relative inline-flex h-14 items-center px-3 text-sm transition-colors',
                        isActive
                          ? 'font-semibold text-primary'
                          : 'text-muted-foreground hover:text-foreground',
                        isActive &&
                          'after:absolute after:inset-x-2 after:bottom-0 after:h-0.5 after:rounded-t after:bg-primary after:content-[""]'
                      )}
                    >
                      {item.label}
                      {showCount && (
                        <span
                          className={cn(
                            'ml-1.5 inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-1 text-[11px] font-bold leading-none text-white',
                            countColor
                          )}
                        >
                          {uploadCount}
                        </span>
                      )}
                    </button>
                  )
                })}
              </nav>

              <div className="ml-auto flex items-center gap-2 sm:ml-1">
                <span className="hidden text-sm text-muted-foreground md:block">{contactName}</span>
                <DropdownMenu>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <DropdownMenuTrigger asChild>
                        <button className="rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                          <Avatar>
                            <AvatarFallback>{initials}</AvatarFallback>
                          </Avatar>
                        </button>
                      </DropdownMenuTrigger>
                    </TooltipTrigger>
                    {accountName && <TooltipContent>{accountName}</TooltipContent>}
                  </Tooltip>
                  <DropdownMenuContent align="end" className="w-60">
                    <div className="px-2 py-1.5">
                      <div className="truncate text-sm font-semibold">{accountName}</div>
                      <div className="truncate text-xs text-muted-foreground">{contactEmail}</div>
                    </div>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onSelect={() => requestNavigation('/profile')}>
                      My Profile
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={() => setReportOpen(true)}>
                      Report an Issue
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onSelect={() => requestSignOut()}
                      className="text-destructive focus:text-destructive"
                    >
                      Log Out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </header>

          {/* Report an Issue dialog */}
          <Dialog open={reportOpen} onOpenChange={setReportOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Report an Issue</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                <p>
                  For immediate assistance, call our 24-hour emergency line:{' '}
                  <a className="text-primary hover:underline" href="tel:9492161063">(949) 216-1063</a>.
                </p>
                <p>
                  If your issue is not an emergency, please send an email to{' '}
                  <a className="text-primary hover:underline" href="mailto:aberfast@abercap.com">aberfast@abercap.com</a>.
                  We'll respond during normal business hours.
                </p>
                <p>
                  For general feedback on Aberdeen Hub, please email{' '}
                  <a className="text-primary hover:underline" href="mailto:admin@aberdeen.io">admin@aberdeen.io</a>.
                </p>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setReportOpen(false)}>Close</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Page content */}
          <main className="flex-1 px-3 py-3 sm:px-4 sm:py-4">
            <C {...props} />
          </main>

          <footer className="border-t py-3 text-center">
            <a
              href="https://aberdeen.io"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-muted-foreground hover:underline"
            >
              {new Date().getFullYear()} © Aberdeen Hub
            </a>
          </footer>
        </div>
      )}
    />
  )
}

const mapStateToProps = (state) => {
  const {
    accountName,
    contactName,
    contactEmail,
    showUploadsTab
  } = state.stores.auth

  const { uploads } = state.stores.uploads

  return {
    uploads,
    accountName,
    contactName,
    contactEmail,
    showUploadsTab
  }
}

const mapDispatchToProps = dispatch => ({
  ...bindActionCreators({ requestSignOut, requestNavigation }, dispatch)
})

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(AuthRoute))
